import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

interface LocationOption {
  value: string;
  label: string;
  delivery_price?: number;
  estimated_days?: number;
}

interface SimpleLocationSelectorProps {
  selectedStateRegion: string;
  selectedTownship: string;
  onStateRegionChange: (stateRegion: string) => void;
  onTownshipChange: (township: string) => void;
  disabled?: boolean;
  showDeliveryInfo?: boolean;
  className?: string;
}

const SimpleLocationSelector: React.FC<SimpleLocationSelectorProps> = ({
  selectedStateRegion,
  selectedTownship,
  onStateRegionChange,
  onTownshipChange,
  disabled = false,
  showDeliveryInfo = false,
  className = ''
}) => {
  const [stateRegions, setStateRegions] = useState<LocationOption[]>([]);
  const [townships, setTownships] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStateRegions();
  }, []);

  useEffect(() => {
    if (selectedStateRegion) {
      loadTownships(selectedStateRegion);
    } else {
      setTownships([]);
    }
  }, [selectedStateRegion]);

  const loadStateRegions = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/locations/state-regions');
      
      // Fix: API returns data directly in response.data, not response.data.data
      const processedRegions = Array.isArray(response.data) ? response.data : [];
      
      setStateRegions(processedRegions);
    } catch (error: any) {
      setError('Failed to load available regions');
    } finally {
      setLoading(false);
    }
  };

  const loadTownships = async (stateRegion: string) => {
    try {
      setLoading(true);
      const response = await apiService.get(`/locations/townships?state_region=${encodeURIComponent(stateRegion)}`);
      
      // Fix: API returns data directly in response.data, not response.data.data
      const processedTownships = Array.isArray(response.data) ? response.data : [];
      setTownships(processedTownships);
    } catch (error: any) {
      setError('Failed to load townships');
    } finally {
      setLoading(false);
    }
  };

  const handleStateRegionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onStateRegionChange(value);
    onTownshipChange(''); // Clear township when region changes
  };

  const handleTownshipChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onTownshipChange(e.target.value);
  };

  const selectedTownshipData = townships.find(t => t.value === selectedTownship);

  return (
    <div className={className}>
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {/* State/Region Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            State/Region *
          </label>
          <select
            value={selectedStateRegion}
            onChange={handleStateRegionChange}
            disabled={disabled || loading}
            className="block w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-gray-400 focus:ring-4 focus:ring-gray-100 transition-all duration-200"
          >
            <option value="">Select State/Region</option>
            {stateRegions.map((region, index) => (
                <option key={region.value || index} value={region.value}>
                  {region.label}
                </option>
            ))}
          </select>
        </div>

        {/* Township Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Township *
          </label>
          <select
            value={selectedTownship}
            onChange={handleTownshipChange}
            disabled={disabled || loading || !selectedStateRegion}
            className="block w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-gray-400 focus:ring-4 focus:ring-gray-100 transition-all duration-200"
          >
            <option value="">
              {!selectedStateRegion ? 'Select State/Region first' : 'Select Township'}
            </option>
            {townships.map((township) => (
              <option key={township.value} value={township.value}>
                {township.label}
              </option>
            ))}
          </select>
        </div>

        {/* Delivery Info */}
        {showDeliveryInfo && selectedTownshipData && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">Delivery Fee</p>
                <p className="text-lg font-bold text-blue-600">
                  {selectedTownshipData.delivery_price} MMK
                </p>
              </div>
              {selectedTownshipData.estimated_days && (
                <div className="text-right">
                  <p className="text-sm font-medium text-blue-900">Estimated Delivery</p>
                  <p className="text-sm text-blue-600">
                    {selectedTownshipData.estimated_days} days
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-2">
            <div className="inline-flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
              <span className="ml-2 text-sm text-gray-600">Loading...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleLocationSelector;
